import { DatePipe, LowerCasePipe } from '@angular/common';
import { Component, computed, effect, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { GamePlayComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/game-play/game-play.component';
import { IKpiCard } from '@cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { AnalyticsDetailsTableComponent } from '@cta-analytics-dialog/deliverable-analytics-content/analytics-details-table/analytics-details-table.component';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { LineGraphComponent } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/line-graph/line-graph.component';
import { ILineGraphInputData } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/line-graph/line-graph.interface';
import { EAnalyticsStatus } from '@enums/campaign-analytics.enum';
import { EDeliverableType } from '@enums/campaign.enum';
import { IGamePlayAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { EmbeddedVideoPlayerComponent } from '@shared/components/embedded-video-player/embedded-video-player.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { StreamDetailsDeliverableBaseDirective } from '../../stream-details-deliverable-base.directive';
import { IGamePlayStreamAnalyticsTableData } from './game-play-stream-analytics.interface';

@Component({
  imports: [
    DatePipe,
    TranslateModule,
    LineGraphComponent,
    AnalyticsDetailsTableComponent,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    EmbeddedVideoPlayerComponent,
    GamePlayComponent,
    DotsSpinnerComponent,
    IconComponent,
    LowerCasePipe,
  ],
  providers: [DeliverableFindingsStore],
  selector: 'app-game-play-stream-analytics',
  templateUrl: './game-play-stream-analytics.component.html',
})
export class GamePlayStreamAnalyticsComponent extends StreamDetailsDeliverableBaseDirective<
  IGamePlayStreamAnalyticsTableData,
  IGamePlayAnalytics
> {
  public override readonly kpiCardData: IKpiCard | null = null;

  public readonly totalGamePlayDurationKpiCardData: IKpiCard = {
    icon: 'clock.svg',
    id: 'totalGamePlayDuration',
    title: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.gamePlay.duration'),
    totalDuration: '0:00',
  };

  public readonly gameNameKpiCardData: IKpiCard = {
    icon: 'game.svg',
    id: 'gameName',
    image: null,
    name: null,
    title: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.gamePlay.gameName'),
  };

  public override readonly displayedColumns: string[] = ['start', 'end', 'gameName', 'actions'];
  public override readonly dataSource: MatTableDataSource<IGamePlayStreamAnalyticsTableData> = new MatTableDataSource();

  public readonly totalGamePlayDuration = signal<string>('0:00');
  public readonly gameIcon = signal<string | null>(null);
  public override readonly deliverableType = EDeliverableType.gamePlay;

  public override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.gamePlay?.find((g) => g.deliverableId === deliverableId);
    }

    return null;
  });

  constructor() {
    super();

    effect(() => {
      if (this.executionAnalyticsWithFindings()) {
        this.setStatus();
        this.calculateTotalGamePlayDuration();
        this.setStreamData();
        this.setGameName();
        this.setGameIcon();
      }
    });

    effect(() => {
      this.manualFindingStatusStore?.serverRemovedIds();

      if (this.executionAnalyticsWithFindings() && this.timelineData()) {
        this.prepareChartAndTableData();
      }
    });

    effect(() => {
      if (this.executionAnalyticsWithFindings() && this.deliverableId()) {
        const gamePlay = this.currentDeliverable();

        this.keywords = gamePlay?.gameName ? [gamePlay.gameName] : [];
      }
    });
  }

  public override onDataPointClick(clickedPointId: string): void {
    this.selectedDataId.set(null);
    const baseId = clickedPointId.replace(/-(start|end)$/, '');

    const gamePlayEntry = this.dataSource.data.find((data) => data.id === baseId);

    if (!gamePlayEntry) {
      return;
    }

    const isEndPoint = clickedPointId.endsWith('-end');
    const timestamp = isEndPoint ? gamePlayEntry.end : gamePlayEntry.start;

    if (timestamp && !this.usesExternalVideoLink()) {
      this.twitchStreamDetailsService.seekToTimestamp(timestamp, this.streamStartTime, this.seekTime);
    }

    setTimeout(() => {
      this.selectedDataId.set(baseId);
    });
  }

  public override onRowClick(event: Event, element: IGamePlayStreamAnalyticsTableData): void {
    event.stopPropagation();

    const timestamp = element.start || element.end;

    if (!timestamp) {
      return;
    }

    if (this.usesExternalVideoLink()) {
      this.openTimestampedVideoUrl(this.getStreamVideoTimeSeconds(timestamp));

      return;
    }

    this.twitchStreamDetailsService.seekToTimestamp(timestamp, this.streamStartTime, this.seekTime);
  }

  protected override setCounts(): void {
    return;
  }

  protected override setKeywordList(): void {
    return;
  }

  protected override prepareChartAndTableData(): void {
    const tableData: IGamePlayStreamAnalyticsTableData[] = [];
    const dotGraphData: ILineGraphInputData[] = [];
    const currentGamePlayAnalytics = this.currentDeliverable();

    const timelineData = this.timelineData() || [];
    const removedIds = this.manualFindingStatusStore?.serverRemovedIds() ?? new Set<string>();
    const findings = (currentGamePlayAnalytics?.findings || []).filter((f) => !removedIds.has(f.id));
    const gameName = currentGamePlayAnalytics?.gameName || this.translate.instant('shared.unknownGame');

    const { sortedDictionary } = this.graphUtilities.processTimelineData(
      timelineData,
      this.stream()?.executionTime,
      this.stream()?.end,
    );
    const gameIcon = currentGamePlayAnalytics?.boxArtUrl || null;

    findings.forEach((finding) => {
      const { id } = finding;
      const startTimestamp = this.dateUtils.parseDateTimeFromApi(finding.start).getTime();
      const endTimestamp = this.dateUtils.parseDateTimeFromApi(finding.end).getTime();

      const interpolatedViewCount = this.graphUtilities.getInterpolatedViewCount(sortedDictionary, startTimestamp);
      const interpolatedViewCountEnd = this.graphUtilities.getInterpolatedViewCount(sortedDictionary, endTimestamp);

      dotGraphData.push({
        id: `${id}-start`,
        status: currentGamePlayAnalytics!.status as EAnalyticsStatus,
        xAxisValue: startTimestamp,
        yAxisValue: interpolatedViewCount,
      });

      dotGraphData.push({
        id: `${id}-end`,
        status: currentGamePlayAnalytics!.status as EAnalyticsStatus,
        xAxisValue: endTimestamp,
        yAxisValue: interpolatedViewCountEnd,
      });

      tableData.push({
        end: finding.end,
        findingParentId: finding.parentId,
        gameIcon,
        gameName,
        id,
        start: finding.start,
      });
    });

    this.dataSource.data = tableData;
    this.dataCount.set(tableData.length);
    this.dotGraphData.set([...dotGraphData].sort((a, b) => a.xAxisValue - b.xAxisValue));
  }

  private setGameName(): void {
    const gamePlay = this.currentDeliverable();
    this.gameNameKpiCardData.name = gamePlay?.gameName || this.translate.instant('shared.unknownGame');
  }

  private calculateTotalGamePlayDuration(): void {
    const currentGamePlayAnalytics = this.currentDeliverable();

    if (!currentGamePlayAnalytics?.totalPlayTime) {
      return this.updateGamePlayDuration('0:00');
    }

    // const totalDurationMs = entries.reduce(
    //   (total, { start, end }) =>
    //     total +
    //     (this.dateUtils.parseDateTimeFromApi(end).getTime() - this.dateUtils.parseDateTimeFromApi(start).getTime()),
    //   0,
    // );

    const formattedDuration = this.durationUtils.getDuration(currentGamePlayAnalytics?.totalPlayTime, 'minutes', true);
    this.updateGamePlayDuration(formattedDuration);
  }

  private updateGamePlayDuration(duration: string): void {
    this.totalGamePlayDuration.set(duration);
    this.totalGamePlayDurationKpiCardData.totalDuration = duration;
  }

  private setGameIcon(): void {
    const currentGamePlayAnalytics = this.currentDeliverable();

    if (!currentGamePlayAnalytics) {
      this.gameIcon.set(null);
      this.gameNameKpiCardData.image = null;

      return;
    }

    const boxArtUrl = currentGamePlayAnalytics.boxArtUrl || null;

    this.gameIcon.set(boxArtUrl);
    this.gameNameKpiCardData.image = boxArtUrl;
  }
}
